import { useEffect, useRef, useState, type RefObject } from "react";
import { ChevronDown, ChevronUp, Search, ArrowRight } from "lucide-react";
import { itx } from "../../../src/translationItx/itx";
import c1_state from "../model/C1_State";
import { C1_EventKey } from "../C1_EventKey";

export type PaletteSearchChapter = {
  id: string;
  name: string;
  items: Array<{ id: string; name: string }>;
};

export function paletteSearchMatches(chapters: PaletteSearchChapter[], query: string) {
  const normalize = (text: string) => text.replace(/\u00ad/g, "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
  const needle = normalize(query.trim());
  if (!needle) return [];
  const matches = chapters.flatMap(chapter => [
    ...(normalize(chapter.name).includes(needle) ? [{ chapterId: chapter.id, itemId: undefined as string | undefined }] : []),
    ...chapter.items.filter(item => normalize(item.name).includes(needle)).map(item => ({ chapterId: chapter.id, itemId: item.id })),
  ]);
  const itemIdsOutsideAll = new Set(matches
    .filter(hit => hit.chapterId !== "all" && hit.itemId !== undefined)
    .map(hit => hit.itemId));
  const itemMatches = matches.filter(hit => hit.itemId !== undefined &&
    (hit.chapterId !== "all" || !itemIdsOutsideAll.has(hit.itemId)));
  const chaptersWithItems = new Set(itemMatches.map(hit => hit.chapterId));
  return matches.filter(hit => hit.itemId === undefined
    ? !chaptersWithItems.has(hit.chapterId)
    : hit.chapterId !== "all" || !itemIdsOutsideAll.has(hit.itemId));
}

/** Scrolls only the palette list so search navigation cannot move the app viewport. */
export function scrollPaletteSearchHit(container: HTMLElement, heading: HTMLElement, target: HTMLElement) {
  const viewportTop = container.getBoundingClientRect().top + container.clientTop;
  const headingTop = heading.getBoundingClientRect().top - viewportTop + container.scrollTop;
  const targetRect = target.getBoundingClientRect();
  const targetTop = targetRect.top - viewportTop + container.scrollTop;
  const targetBottom = targetRect.bottom - viewportTop + container.scrollTop;
  let top = headingTop;
  if (target !== heading) {
    if (targetBottom > top + container.clientHeight) top = targetBottom - container.clientHeight;
    if (targetTop < top) top = targetTop;
  }
  container.scrollTo({ top: Math.max(0, Math.min(top, container.scrollHeight - container.clientHeight)), behavior: "smooth" });
}

export function C1_PaletteSearch({ chapters, rootRef, openChapter, selectItem, clearSelection, toggleAll, allOpen }: {
  chapters: PaletteSearchChapter[];
  rootRef: RefObject<HTMLElement>;
  openChapter: (id: string, selectChapter: boolean) => void;
  selectItem?: (chapterId: string, itemId: string) => void;
  clearSelection?: () => void;
  toggleAll: () => void;
  allOpen: boolean;
}) {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState({ query: "", matches: [] as ReturnType<typeof paletteSearchMatches>, index: 0, request: 0 });
  const { matches, index } = search;
  const hit = matches.length ? matches[index % matches.length] : undefined;
  const openRef = useRef(openChapter);
  openRef.current = openChapter;
  const selectRef = useRef(selectItem);
  selectRef.current = selectItem;
  const clearRef = useRef(clearSelection);
  clearRef.current = clearSelection;

  useEffect(() => {
    if (!hit) {
      if (search.query) clearRef.current?.();
      return;
    }
    openRef.current(hit.chapterId, hit.itemId === undefined);
    if (hit.itemId !== undefined) selectRef.current?.(hit.chapterId, hit.itemId);
    let scrollFrame: number | undefined;
    const frame = requestAnimationFrame(() => {
      scrollFrame = requestAnimationFrame(() => {
        const chapter = Array.from(rootRef.current?.querySelectorAll<HTMLElement>("[data-palette-chapter]") ?? [])
          .find(element => element.dataset.paletteChapter === hit.chapterId);
        const target = hit.itemId === undefined ? chapter?.querySelector<HTMLElement>("[data-palette-heading]") ?? chapter : Array.from(chapter?.querySelectorAll<HTMLElement>("[data-palette-item]") ?? [])
          .find(element => element.dataset.paletteItem === hit.itemId);
        if (!target) return;
        const heading = chapter?.querySelector<HTMLElement>("[data-palette-heading]") ?? chapter;
        const container = rootRef.current?.querySelector<HTMLElement>(".toolbarInner");
        if (container && heading) scrollPaletteSearchHit(container, heading, target);
        target.classList.add("C1_paletteSearchHit");
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      if (scrollFrame !== undefined) cancelAnimationFrame(scrollFrame);
      rootRef.current?.querySelectorAll(".C1_paletteSearchHit").forEach(element => element.classList.remove("C1_paletteSearchHit"));
    };
  }, [search.request, index, hit?.chapterId, hit?.itemId, rootRef]);

  const next = () => {
    const submittedQuery = query.trim();
    if (!submittedQuery) return;
    setSearch(current => ({ query: submittedQuery,
      matches: paletteSearchMatches(chapters, submittedQuery),
      index: current.query === submittedQuery ? current.index + 1 : 0,
      request: current.request + 1,
    }));
  };
  // Log the effective query once typing pauses, instead of every keystroke.
  useEffect(() => {
    if (!search.query) return;
    const timer = setTimeout(() => {
      c1_state.eventLogger.addEvent(C1_EventKey.PaletteSearchUsed, {
        query: search.query,
        matchCount: search.matches.length,
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [search.query, search.matches.length]);
  return (
    <div className="C1_paletteSearch" role="search" onPointerDown={event => event.stopPropagation()}>
      <div className="C1_paletteSearchField">
        <Search size={15} aria-hidden="true" />
        <input className="dockControl" type="search" aria-label={itx("c1.ui.paletteSearch", "Kapitel und Objekte suchen")}
          placeholder={itx("c1.ui.paletteSearchPlaceholder", "Suchen…")} value={query}
          onChange={event => {
            const value = event.target.value;
            const automaticQuery = value.trim().length >= 2 ? value.trim() : "";
            setQuery(value);
            setSearch(current => ({ query: automaticQuery,
              matches: automaticQuery ? paletteSearchMatches(chapters, automaticQuery) : [],
              index: 0,
              request: current.request + 1,
            }));
          }}
          onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); next(); } }} />
        <span className="C1_paletteSearchCount" aria-live="polite">{search.query ? `${matches.length ? index % matches.length + 1 : 0}/${matches.length}` : ""}</span>
        <button className="dockIconButton" type="button" disabled={!query.trim()} onClick={next}
          title={itx("c1.ui.paletteSearchNext", "Nächster Treffer")} aria-label={itx("c1.ui.paletteSearchNext", "Nächster Treffer")}>
          <ArrowRight size={16} />
        </button>
      </div>
      {chapters.length > 1 && <button className="dockIconButton" type="button" onClick={toggleAll} aria-expanded={allOpen}
        title={allOpen ? itx("c1.ui.paletteCloseAll", "Alle Kapitel schließen") : itx("c1.ui.paletteOpenAll", "Alle Kapitel öffnen")}
        aria-label={allOpen ? itx("c1.ui.paletteCloseAll", "Alle Kapitel schließen") : itx("c1.ui.paletteOpenAll", "Alle Kapitel öffnen")}>
        {allOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>}
    </div>
  );
}
